import { Trophy, Users, Target, Star, MapPin, Calendar, Repeat, Sparkles, ArrowRight, LogIn, Link as LinkIcon } from 'lucide-react';
import { Button } from '../../../components/ui/button';

// Lists saved in the Homepage Editor win (even when every item is hidden); older configs fall back.
export const shownItems = (arr, fallback) => (Array.isArray(arr) ? arr.filter((x) => x && !x.hidden) : fallback);

const ICONS = { trophy: Trophy, users: Users, target: Target, star: Star, 'map-pin': MapPin, calendar: Calendar, repeat: Repeat };
const TONES = {
  blue: ['bg-blue-500/20', 'text-blue-400'],
  emerald: ['bg-emerald-500/20', 'text-emerald-400'],
  amber: ['bg-amber-500/20', 'text-amber-400'],
  red: ['bg-red-500/20', 'text-red-400'],
  purple: ['bg-purple-500/20', 'text-purple-400'],
  teal: ['bg-teal-500/20', 'text-teal-400'],
  indigo: ['bg-indigo-500/20', 'text-indigo-400'],
  violet: ['bg-violet-500/20', 'text-violet-400'],
};

export const DEFAULT_HERO_CARDS = [
  { icon: 'trophy', title: 'Expert Coaches', subtitle: 'Professional trainers', color: 'blue' },
  { icon: 'users', title: 'Small Groups', subtitle: 'Individual attention', color: 'emerald' },
  { icon: 'target', title: 'All Levels', subtitle: 'Beginner to advanced', color: 'amber' },
  { icon: 'star', title: 'Trial Class', subtitle: 'Try before you join', color: 'red' },
];

export const DEFAULT_QUICK_LINKS = [
  { label: 'Sign In', action: 'signin' },
  { label: 'Free Trial', action: 'trial' },
  { label: 'Our Players', action: 'url', url: '/our-players' },
];

export function HeroFeatureCards({ cards, centered }) {
  if (!cards.length) return null;
  return (
    <div className={`grid ${cards.length === 1 ? 'grid-cols-1 w-fit' : 'grid-cols-2'} gap-4 py-4 ${centered ? 'max-w-xl mx-auto text-left' : ''}`} data-testid="hero-feature-cards">
      {cards.map((c, idx) => {
        const Icon = ICONS[c.icon] || Star;
        const [bg, fg] = TONES[c.color] || TONES.blue;
        return (
          <div key={idx} className="flex items-start gap-3" data-testid={`hero-feature-card-${idx}`}>
            <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-5 h-5 ${fg}`} />
            </div>
            <div>
              <p className="font-medium text-white">{c.title}</p>
              {c.subtitle && <p className="text-sm text-slate-400">{c.subtitle}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const LINK_STYLE = {
  signin: [LogIn, 'text-blue-400 hover:text-blue-300'],
  trial: [Star, 'text-emerald-400 hover:text-emerald-300'],
  signup: [Sparkles, 'text-emerald-400 hover:text-emerald-300'],
  url: [LinkIcon, 'text-amber-400 hover:text-amber-300'],
};

export function HeroCta({ content, onAction }) {
  const links = shownItems(content.quick_links, DEFAULT_QUICK_LINKS);
  return (
    <div className="relative w-full" data-testid="hero-cta">
      <Button
        size="lg"
        className="w-full h-14 bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 text-white font-semibold text-lg shadow-lg shadow-emerald-500/25"
        onClick={() => onAction(content.cta_primary_action || 'signup', content.cta_primary_url)}
        data-testid="hero-get-started-btn"
      >
        <Sparkles className="w-5 h-5 mr-2" />
        {content.cta_primary_text || 'Get Started'}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
      {links.length > 0 && (
        <div className="flex flex-wrap justify-center items-center gap-4 mt-4" data-testid="hero-quick-links">
          {links.map((l, idx) => {
            const [Icon, cls] = LINK_STYLE[l.action] || LINK_STYLE.url;
            const PlayerIcon = l.url === '/our-players' ? Users : Icon;
            return (
              <span key={idx} className="flex items-center gap-4">
                {idx > 0 && <span className="text-slate-600">|</span>}
                <button onClick={() => onAction(l.action || 'url', l.url)} className={`text-sm ${cls} flex items-center gap-1 transition-colors`} data-testid={`hero-quick-link-${idx}`}>
                  <PlayerIcon className="w-4 h-4" />
                  {l.label}
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
